import React, { useState, useRef, useCallback } from 'react';  
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from 'react-image-crop';  
import 'react-image-crop/dist/ReactCrop.css';  
import { Button } from './button';  
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog';  

interface ImageCropperProps {  
  open: boolean;  
  onClose: () => void;  
  imageSrc: string;  
  onCropComplete: (croppedImage: string) => void;  
}  

const ImageCropper = ({ open, onClose, imageSrc, onCropComplete }: ImageCropperProps) => {  
  // Set crop to a 1280x720 aspect ratio  
  const aspect = 1280 / 720;  

  const [crop, setCrop] = useState<Crop>({  
    unit: '%',  
    width: 90,      // You can adjust these starting sizes as needed  
    height: (90 / aspect), // Maintain aspect ratio  
    x: 0,  
    y: 0,  
  });  
  
  const imgRef = useRef<HTMLImageElement>(null);  

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {  
    const { width, height } = e.currentTarget;  
    const crop = centerCrop(  
      makeAspectCrop(  
        {  
          unit: '%',  
          width: 90, // You can choose to start with 90% width  
        },  
        aspect, // Use the aspect ratio here  
        width,  
        height  
      ),  
      width,  
      height  
    );  
    setCrop(crop);  
  }, [aspect]);  

  const getCroppedImage = useCallback(() => {  
    if (!imgRef.current) return;  

    const image = imgRef.current;  
    const canvas = document.createElement('canvas');  
    const scaleX = image.naturalWidth / image.width;  
    const scaleY = image.naturalHeight / image.height;  
    const ctx = canvas.getContext('2d');  

    if (!ctx) return;  

    canvas.width = 1280;  // Set canvas width to 1280  
    canvas.height = 720;   // Set canvas height to 720  

    ctx.drawImage(  
      image,  
      crop.x * scaleX,  
      crop.y * scaleY,  
      crop.width * scaleX,  
      crop.height * scaleY,  
      0,  
      0,  
      canvas.width,  
      canvas.height  
    );  

    const base64Image = canvas.toDataURL('image/jpeg');  
    onCropComplete(base64Image);  
    onClose();  
  }, [crop, onCropComplete, onClose]);  

  return (  
    <Dialog open={open} onOpenChange={onClose}>  
      <DialogContent className="max-w-lg">  
        <DialogHeader>  
          <DialogTitle>Crop Image</DialogTitle>  
          <DialogDescription>  
            Adjust the crop area for your image to 1280x720 pixels.  
          </DialogDescription>  
        </DialogHeader>  
        <div className="my-5 flex justify-center">  
          <ReactCrop  
            crop={crop}  
            onChange={(c) => setCrop(c)}  
            aspect={aspect} // Set the cropping aspect ratio to landscape  
            className="max-h-[400px]"  
          >  
            {imageSrc && (  
              <img ref={imgRef} src={imageSrc} alt="Crop preview" onLoad={onImageLoad} />  
            )}  
          </ReactCrop>  
        </div>  
        <DialogFooter>  
          <Button variant="outline" onClick={onClose}>Cancel</Button>  
          <Button onClick={getCroppedImage}>Save</Button>  
        </DialogFooter>  
      </DialogContent>  
    </Dialog>  
  );  
};  

export { ImageCropper };  